import React, { useEffect, useState } from 'react';
import {
    Button,
    Card,
    Form,
    Input,
    Modal,
    Select,
    Space,
    Table,
    Tag,
    Typography,
    message
} from 'antd';
import { EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import {
    getOrganizationManagementCenters,
    postOrganizationManagementCenter,
    putOrganizationManagementCenter
} from '../store/user/thunks';

const { Text } = Typography;

interface Center {
    id: string;
    name: string;
    description?: string | null;
    address?: string | null;
    latitud?: string | null;
    longitude?: string | null;
    status?: string;
}

interface Props {
    organizationId: string;
    getOrganizationManagementCenters?: any;
    postOrganizationManagementCenter?: any;
    putOrganizationManagementCenter?: any;
}

const normalizeCenters = (source: any): Center[] => {
    const values = Array.isArray(source) ? source : source?.centers || source?.data || [];
    return Array.isArray(values) ? values.map((item: any) => ({
        id: String(item.id || item.Id || ''),
        name: String(item.name || item.Name || 'Centro'),
        description: item.description ?? item.Description,
        address: item.address ?? item.Address,
        latitud: item.latitud ?? item.Latitud,
        longitude: item.longitude ?? item.Longitude,
        status: String(item.status || item.Status || 'ACTIVO')
    })).filter(item => item.id) : [];
};

const OrganizationCentersManagement: React.FC<Props> = props => {
    const [form] = Form.useForm();
    const [centers, setCenters] = useState<Center[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingCenter, setEditingCenter] = useState<Center | null>(null);

    const loadCenters = async () => {
        setLoading(true);
        try {
            const response = await props.getOrganizationManagementCenters?.(props.organizationId);
            setCenters(normalizeCenters(response));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadCenters();
        // The organization id identifies the scope of this settings tab.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.organizationId]);

    const openCreate = () => {
        setEditingCenter(null);
        form.resetFields();
        form.setFieldsValue({ status: 'ACTIVO' });
        setModalOpen(true);
    };

    const openEdit = (center: Center) => {
        setEditingCenter(center);
        form.setFieldsValue({
            name: center.name,
            description: center.description || '',
            address: center.address || '',
            latitud: center.latitud || '',
            longitude: center.longitude || '',
            status: center.status || 'ACTIVO'
        });
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditingCenter(null);
        form.resetFields();
    };

    const submit = async () => {
        const values = await form.validateFields();
        const payload = {
            Name: values.name,
            Description: values.description || null,
            Address: values.address || null,
            Latitud: values.latitud || null,
            Longitude: values.longitude || null,
            Status: values.status
        };
        const response = editingCenter
            ? await props.putOrganizationManagementCenter?.(editingCenter.id, payload)
            : await props.postOrganizationManagementCenter?.(props.organizationId, payload);
        if (response) {
            message.success(editingCenter ? 'Sucursal actualizada correctamente.' : 'Sucursal creada correctamente.');
            closeModal();
            await loadCenters();
        }
    };

    return <Card
        title="Sucursales"
        extra={<Space>
            <Button icon={<ReloadOutlined />} onClick={loadCenters}>Actualizar</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Nueva sucursal</Button>
        </Space>}
    >
        <Text type="secondary">Administre los centros médicos disponibles para citas, horarios y especialidades.</Text>
        <Table
            style={{ marginTop: 16 }}
            rowKey="id"
            loading={loading}
            dataSource={centers}
            pagination={false}
            columns={[
                {
                    title: 'Sucursal',
                    dataIndex: 'name',
                    render: (value: string, row: Center) => <Space direction="vertical" size={0}>
                        <Text strong>{value}</Text>
                        <Text type="secondary">{row.address || 'Dirección no registrada'}</Text>
                    </Space>
                },
                { title: 'Descripción', dataIndex: 'description', render: (value: string) => value || 'Sin descripción' },
                { title: 'Estado', dataIndex: 'status', width: 130, render: (value: string) => <Tag color={value === 'ACTIVO' ? 'green' : 'default'}>{value}</Tag> },
                {
                    title: '',
                    key: 'actions',
                    width: 120,
                    align: 'right' as const,
                    render: (_: unknown, row: Center) => <Button icon={<EditOutlined />} onClick={() => openEdit(row)}>Editar</Button>
                }
            ]}
        />
        <Modal
            open={modalOpen}
            title={editingCenter ? 'Editar sucursal' : 'Nueva sucursal'}
            okText={editingCenter ? 'Actualizar' : 'Crear'}
            cancelText="Cancelar"
            onOk={submit}
            onCancel={closeModal}
            destroyOnHidden
        >
            <Form form={form} layout="vertical">
                <Form.Item label="Nombre" name="name" rules={[{ required: true, message: 'Ingrese el nombre de la sucursal' }]}>
                    <Input placeholder="Sucursal principal" />
                </Form.Item>
                <Form.Item label="Descripción" name="description"><Input.TextArea rows={2} /></Form.Item>
                <Form.Item label="Dirección" name="address"><Input /></Form.Item>
                <Space.Compact style={{ width: '100%' }}>
                    <Form.Item label="Latitud" name="latitud" style={{ width: '50%' }}><Input /></Form.Item>
                    <Form.Item label="Longitud" name="longitude" style={{ width: '50%' }}><Input /></Form.Item>
                </Space.Compact>
                <Form.Item label="Estado" name="status" rules={[{ required: true }]}>
                    <Select options={[{ value: 'ACTIVO', label: 'Activo' }, { value: 'INACTIVO', label: 'Inactivo' }]} />
                </Form.Item>
            </Form>
        </Modal>
    </Card>;
};

export default connect(null, {
    getOrganizationManagementCenters,
    postOrganizationManagementCenter,
    putOrganizationManagementCenter
})(OrganizationCentersManagement);
